import { ReactNode } from 'react';
import styled from 'styled-components';

import { nepal } from '@paradigm/design-system/src/colors';

import KillQuoteButton from '#/components/UnifiedRfqs/rfqs/action/KillQuoteButton';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import {
  IVCell,
  KillCell,
  PriceCell,
  QtyCell,
  RowAsk,
  RowBid,
} from './QuotesRows';
import type { Product } from '@paradigm/features/src/products/types';

interface QuotesTableProps {
  readonly isOffer: boolean;
  readonly hasOwnQuotes: boolean;
  readonly children: ReactNode;
  readonly contractCurrency: string | undefined;
  readonly quoteCurrency: Product['quoteCurrency'];
}

export default function QuotesTable({
  isOffer,
  hasOwnQuotes,
  children,
  contractCurrency,
  quoteCurrency,
}: QuotesTableProps) {
  const isMobile = useIsMobile();
  const displayIV = !isMobile;

  const priceCurrency =
    quoteCurrency != null ? quoteCurrency : contractCurrency;

  const dataTestId = isOffer ? 'asks-quotes-table' : 'bids-quotes-table';

  const rowContent = isOffer ? (
    <RowAsk>
      {displayIV && <IVCell>IV</IVCell>}
      <QtyCell>
        Qty <CurCell>{contractCurrency}</CurCell>
      </QtyCell>
      {hasOwnQuotes && (
        <KillCell>
          <KillQuoteButton title="Kill by side" price={null} side="SELL" />
        </KillCell>
      )}
      <PriceCell>
        Ask <CurCell>{priceCurrency}</CurCell>
      </PriceCell>
    </RowAsk>
  ) : (
    <RowBid>
      {displayIV && <IVCell>IV</IVCell>}
      <QtyCell>
        Qty <CurCell>{contractCurrency}</CurCell>
      </QtyCell>
      {hasOwnQuotes && (
        <KillCell>
          <KillQuoteButton title="Kill by side" price={null} side="BUY" />
        </KillCell>
      )}
      <PriceCell>
        Bid <CurCell>{priceCurrency}</CurCell>
      </PriceCell>
    </RowBid>
  );
  return (
    <>
      <Header>{isOffer ? 'Asks' : 'Bids'}</Header>
      <Container data-testid={dataTestId}>
        <TableHeader>{rowContent}</TableHeader>
        <BodyContainer>{children}</BodyContainer>
      </Container>
    </>
  );
}

const Container = styled.div`
  text-align: left;
  margin-bottom: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
`;

const Header = styled.p`
  margin: 0;
  margin-bottom: 5px;
  font-size: 0.9rem;
  color: ${nepal};
`;

const TableHeader = styled.div`
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-weight: 500;

  & span {
    color: white;
  }
`;

const BodyContainer = styled.div`
  margin: 5px;
  margin-left: 0;
  overflow: auto;
  height: 120px;
`;

const CurCell = styled.span`
  margin-left: 5px;
  opacity: 0.3;
`;
